import React, { useState } from 'react';
import { Clock, CloudOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { waitMessage } from '../utils/rateLimit';

// Shown by the route guards in place of a page when the session check got no answer
// about the session: the rate limiter, a server fault, or the network. Redirecting to
// /login there told people with a live session that they had been signed out. It asks
// the server again only when Try again is pressed - a retry on a timer would spend the
// rate-limit budget the visitor needs once the window resets.
const AuthUnavailable = () => {
  const { authUnavailable, retryAuth } = useAuth();
  const [checking, setChecking] = useState(false);
  const { rateLimited, retryAfter } = authUnavailable || {};
  const Icon = rateLimited ? Clock : CloudOff;

  const tryAgain = async () => {
    // aria-disabled rather than disabled, so the button keeps keyboard focus while the
    // check runs and is still focused if it fails again.
    if (checking) return;
    setChecking(true);
    await retryAuth();
    setChecking(false);
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-8 text-center">
        <Icon className="mx-auto h-12 w-12 text-gray-400 mb-4" aria-hidden="true" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Can't reach the HR portal right now</h1>
        <p className="text-gray-600 text-sm text-balance" aria-live="polite">
          {rateLimited
            ? `The portal is busy. ${waitMessage(retryAfter)}`
            : "The portal didn't answer. This is usually temporary, so please wait a moment and try again."}
        </p>
        <p className="mt-2 text-gray-500 text-sm">This doesn't mean you've been signed out.</p>
        <button
          type="button"
          onClick={tryAgain}
          aria-disabled={checking}
          className="mt-6 px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 aria-disabled:opacity-60 aria-disabled:cursor-wait"
        >
          {checking ? 'Checking...' : 'Try again'}
        </button>
      </div>
    </main>
  );
};

export default AuthUnavailable;
